import { toUserMessage } from '../../lib/errors.js';
// ProductionsPage — Lavorazioni (output finiti) con catena di tracciabilità.
// Ogni Produzione referenzia uno o più lotti in ingresso (stock_in) e, al salvataggio,
// genera lo stock_out dei lotti consumati + una riga productions (master) con trace_token
// (generato server-side). L'etichetta usa trace_token per il QR di tracciabilità.

import {
  IonBackButton,
  IonAlert,
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import {
  add,
  closeOutline,
  qrCodeOutline,
  snowOutline,
  trashOutline,
} from 'ionicons/icons';
import { useMemo, useRef, useState } from 'react';
import {
  StockCommandV1Schema,
  recipeShelfLifeMinutes,
  type StockCommandMasterEffectV1,
  type StockCommandV1,
} from '@trackeat/shared';
import { SearchPickerModal, PickerTriggerField } from '../../components/SearchPickerModal.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { canCreateFood } from '../../lib/auth/capabilities.js';
import {
  useCollettivaEnabled,
  useEnqueueStockCommand,
  useEvents,
  useMasters,
  useStockProjection,
} from '../../lib/data/queries.js';
import {
  carriedProductionOutputValue,
  createProductionStockLine,
  planProductionFinishedFood,
  productionBatchLot,
  productionReceiptCoordinates,
  productionReverseMovements,
  type ProductionCommandEvent,
} from '../../lib/productionOutput.js';
import { projectedCarryingCost } from '../../lib/stockCarryingCost.js';
import { TRACE_BASE_URL } from '../../lib/supabase.js';

interface Food {
  id: string;
  name: string;
  default_uom: string;
  allergens?: string[];
  recipe_id?: string | null;
  archived_at?: string | null;
}
interface Supplier {
  id: string;
  name: string;
  piva: string | null;
}
interface DocumentMaster {
  id: string;
  document_number: string | null;
  document_date: string | null;
  supplier_id: string | null;
}
interface Production {
  id: string;
  business_id: string;
  output_name: string;
  output_qty: number | null;
  output_uom: string | null;
  produced_at: string;
  expires_at?: string | null;
  shelf_life_minutes?: number | null;
  recipe_id?: string | null;
  inputs: ProductionInputSaved[];
  allergens_aggregated?: string[];
  trace_token: string;
  version?: number | null;
  archived_at?: string | null;
}

interface RecipeRow {
  id: string;
  name: string;
  yield_qty: number;
  yield_uom: string;
  ingredients: Array<{ food_id: string; qty: number; uom: string }>;
  allergens: string[];
  shelf_life_days: number | null;
  chilling_enabled: boolean;
  chilling_sign: 'positive' | 'negative' | null;
  freezing_enabled?: boolean;
  // T-148 — campi abbattimento ereditati dalla ricetta (sola lettura qui:
  // la configurazione vive in RecipesPage, qui li mostriamo nel dettaglio
  // della lavorazione se la ricetta d'origine prevede l'abbattimento).
  chilling_minutes: number | null;
  temp_start_c: number | null;
  temp_end_c: number | null;
  default_shelf_life_minutes: number | null;
  archived_at?: string | null;
  // T-441 (collettiva): se valorizzato, è una VARIANTE dieta (ricetta figlia) →
  // nascosta dal picker di produzione (resta risolvibile per id).
  variant_of_recipe_id?: string | null;
}

function shelfMinutesFromRecipe(r: RecipeRow | undefined): number | null {
  return r ? recipeShelfLifeMinutes(r) : null;
}

// T-148 — Riepilogo testuale delle impostazioni di abbattimento configurate
// SULLA RICETTA (sorgente unica). La produzione non configura più l'abbattimento:
// se la ricetta d'origine lo prevede, lo mostriamo in sola lettura.
function chillingSummaryFromRecipe(r: RecipeRow | undefined): string | null {
  if (!r || !r.chilling_enabled) return null;
  const parts: string[] = [];
  parts.push(r.chilling_sign === 'positive' ? 'Positivo (+90 → +3 °C)' : 'Negativo (+90 → -18 °C)');
  if (r.chilling_minutes != null) {
    const h = Math.floor(r.chilling_minutes / 60);
    const m = r.chilling_minutes % 60;
    parts.push(`${h}h${m.toString().padStart(2, '0')}`);
  }
  if (r.temp_start_c != null || r.temp_end_c != null) {
    const from = r.temp_start_c != null ? `${r.temp_start_c}°C` : '—';
    const to = r.temp_end_c != null ? `${r.temp_end_c}°C` : '—';
    parts.push(`${from} → ${to}`);
  }
  return parts.join(' · ');
}
interface ProductionInputSaved {
  batch_lot?: string | null;
  food_id?: string;
  food_name?: string;
  supplier_id?: string | null;
  supplier_name?: string | null;
  document_id?: string | null;
  qty_used?: number;
  uom?: string;
  allergens?: string[];
  stock_command_id?: string;
  stock_command_event_count?: number;
}

interface StockInEvent {
  id: string;
  business_id?: string | null;
  client_ts: string;
  payload: {
    food_id: string;
    qty: number;
    batch_lot: string | null;
    expiration_date: string | null;
    supplier_id?: string | null;
    document_id?: string | null;
    storage_unit_id?: string | null;
    uom?: string;
    stock_uom?: string;
    stock_unit_cost?: number | null;
    unit_cost?: number | null;
    source_currency?: string | null;
    currency?: string | null;
    business_id?: string | null;
  };
}

interface LotRow {
  key: string;
  food_id: string;
  batch_lot: string | null;
  supplier_id: string | null;
  document_id: string | null;
  storage_unit_id: string | null;
  uom: string;
  available: number;
  expires: string | null;
  unit_cost: number | null;
  currency: string;
}

interface InputDraft {
  key: string;
  lot: LotRow;
  qty_used: string;
}

export function ProductionsPage(): JSX.Element {
  const { profile } = useAuth();
  const canCreateFoods = canCreateFood(profile);
  const collettivaEnabled = useCollettivaEnabled();
  const { data: foods = [] } = useMasters<Food>('foods');
  const { data: suppliers = [] } = useMasters<Supplier>('suppliers');
  const { data: documents = [] } = useMasters<DocumentMaster>('documents');
  const { data: productions = [] } = useMasters<Production>('productions');
  const { data: recipes = [] } = useMasters<RecipeRow>('recipes');
  const { data: stockIn = [] } = useEvents('stock_in', null);
  const { data: stockCommandEvents = [] } = useEvents(undefined, null);
  const enqueueStockCommand = useEnqueueStockCommand();
  const pendingCreateEnvelope = useRef<{
    command: StockCommandV1;
    masterEffects: readonly StockCommandMasterEffectV1[];
  } | null>(null);
  const pendingCancelEnvelope = useRef<{
    productionId: string;
    command: StockCommandV1;
    masterEffects: readonly StockCommandMasterEffectV1[];
  } | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [outputName, setOutputName] = useState('');
  const [outputQty, setOutputQty] = useState('');
  const [outputUom, setOutputUom] = useState('kg');
  const [notes, setNotes] = useState('');
  const [selectedLotKey, setSelectedLotKey] = useState<string>('');
  const [qtyDraft, setQtyDraft] = useState('');
  const [inputDrafts, setInputDrafts] = useState<InputDraft[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [detail, setDetail] = useState<Production | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Production | null>(null);
  const [recipeId, setRecipeId] = useState<string>('');
  const [pickerOpen, setPickerOpen] = useState<null | 'recipe' | 'lot'>(null);
  const [shelfHours, setShelfHours] = useState<string>('72');

  const foodById = useMemo(() => new Map(foods.map((f) => [f.id, f])), [foods]);
  const supplierById = useMemo(() => new Map(suppliers.map((s) => [s.id, s])), [suppliers]);
  const docById = useMemo(() => new Map(documents.map((d) => [d.id, d])), [documents]);
  const recipeById = useMemo(() => new Map(recipes.map((r) => [r.id, r])), [recipes]);
  const canonicalUomByFood = useMemo(() => Object.fromEntries(
    foods
      .filter((food) => food.default_uom.trim() !== '')
      .map((food) => [food.id, food.default_uom.trim()]),
  ), [foods]);
  const { data: stockProjection } = useStockProjection({ canonicalUomByFood });
  const activeProductions = useMemo(() => productions.filter((production) => (
    !production.archived_at
    && production.business_id === profile?.active_business_id
  )), [productions, profile?.active_business_id]);

  // Canonical balance (uncapped) + descriptive metadata from stock-in.
  // Quantity and carrying cost both come exclusively from the projector:
  // using an arbitrary receipt/latest food price would misvalue produced or
  // transferred lots.
  const lots: LotRow[] = useMemo(() => {
    type LotMetadata = Pick<
      LotRow,
      'key' | 'food_id' | 'batch_lot' | 'supplier_id' | 'document_id' | 'storage_unit_id'
    >;
    const metadata = new Map<string, LotMetadata>();
    for (const ev of stockIn as StockInEvent[]) {
      const eventBusinessId = ev.business_id ?? ev.payload.business_id ?? null;
      if (eventBusinessId !== (profile?.active_business_id ?? null)) continue;
      const key = `${ev.payload.food_id}|${ev.payload.batch_lot ?? ''}`;
      const existing = metadata.get(key) ?? {
        key,
        food_id: ev.payload.food_id,
        batch_lot: ev.payload.batch_lot,
        supplier_id: null,
        document_id: null,
        storage_unit_id: null,
      };
      if (!existing.supplier_id && ev.payload.supplier_id) existing.supplier_id = ev.payload.supplier_id;
      if (!existing.document_id && ev.payload.document_id) existing.document_id = ev.payload.document_id;
      if (!existing.storage_unit_id && ev.payload.storage_unit_id) {
        existing.storage_unit_id = ev.payload.storage_unit_id;
      }
      metadata.set(key, existing);
    }
    return stockProjection.balances
      .filter((balance) => balance.availableQty > 0.0001 && !balance.closed)
      .map((balance): LotRow => {
        const key = `${balance.foodId}|${balance.batchLot ?? ''}`;
        const carried = metadata.get(key);
        const carryingCost = projectedCarryingCost(balance);
        return {
          key,
          food_id: balance.foodId,
          batch_lot: balance.batchLot,
          supplier_id: carried?.supplier_id ?? null,
          document_id: carried?.document_id ?? null,
          storage_unit_id: carried?.storage_unit_id ?? null,
          uom: balance.uom,
          available: balance.availableQty,
          expires: balance.expirationDate,
          unit_cost: carryingCost.unitCost,
          currency: carryingCost.currency,
        };
      });
  }, [profile?.active_business_id, stockIn, stockProjection.balances]);

  function resetForm(): void {
    pendingCreateEnvelope.current = null;
    setOutputName('');
    setOutputQty('');
    setOutputUom('kg');
    setNotes('');
    setSelectedLotKey('');
    setQtyDraft('');
    setInputDrafts([]);
    setRecipeId('');
    setShelfHours('72');
  }

  function applyRecipe(rid: string): void {
    setRecipeId(rid);
    const r = recipes.find((x) => x.id === rid);
    if (!r) return;
    setOutputName(r.name);
    setOutputQty(String(r.yield_qty ?? ''));
    setOutputUom(r.yield_uom ?? 'kg');
    const m = shelfMinutesFromRecipe(r);
    if (m) setShelfHours(String(Math.round(m / 60)));
  }

  function addInputDraft(): void {
    if (!selectedLotKey) { setToast('Seleziona un lotto'); return; }
    const lot = lots.find((l) => l.key === selectedLotKey);
    if (!lot) return;
    const qty = Number(qtyDraft.replace(',', '.'));
    if (!Number.isFinite(qty) || qty <= 0) { setToast('Quantità non valida'); return; }
    if (qty > lot.available) { setToast(`Max disponibile: ${lot.available.toFixed(2)} ${lot.uom}`); return; }
    if (inputDrafts.some((d) => d.key === selectedLotKey)) { setToast('Lotto già aggiunto'); return; }
    setInputDrafts((prev) => [...prev, { key: selectedLotKey, lot, qty_used: qtyDraft }]);
    setSelectedLotKey('');
    setQtyDraft('');
  }

  function removeInputDraft(key: string): void {
    setInputDrafts((prev) => prev.filter((d) => d.key !== key));
  }

  async function saveProduction(): Promise<void> {
    if (!profile?.active_tenant_id || !profile?.active_business_id) {
      setToast('Nessun locale attivo'); return;
    }
    // Un errore di esito locale può essere ambiguo: in quel caso si ritenta
    // prima la stessa busta immutabile, senza ricostruirla dallo stato UI.
    if (pendingCreateEnvelope.current) {
      try {
        await enqueueStockCommand.mutateAsync({
          command: pendingCreateEnvelope.current.command,
          master_effects: pendingCreateEnvelope.current.masterEffects,
        });
        setToast('Lavorazione salvata');
        resetForm();
        setModalOpen(false);
      } catch (err) {
        setToast(toUserMessage(err));
      }
      return;
    }
    const name = outputName.trim();
    if (!name) { setToast('Nome lavorazione richiesto'); return; }
    if (inputDrafts.length === 0) { setToast('Aggiungi almeno un ingrediente'); return; }

    const qty = outputQty ? Number(outputQty.replace(',', '.')) : null;
    if (qty != null && (!Number.isFinite(qty) || qty <= 0)) {
      setToast('Quantità prodotta non valida');
      return;
    }
    if (!collettivaEnabled && qty == null) {
      setToast('Quantità prodotta richiesta per caricare il prodotto finito');
      return;
    }
    const recipe = recipeId ? recipeById.get(recipeId) : undefined;
    if (recipeId && (!recipe || recipe.archived_at)) {
      setToast('La ricetta selezionata non è più disponibile');
      return;
    }
    // Build snapshotted inputs array (denormalised — so QR page works even if masters change).
    const currentDrafts: Array<{ lot: LotRow; qtyUsed: number }> = [];
    for (const draft of inputDrafts) {
      const lot = lots.find((candidate) => candidate.key === draft.key);
      const qtyUsed = Number(draft.qty_used.replace(',', '.'));
      if (!lot) {
        setToast('Un lotto selezionato non è più disponibile');
        return;
      }
      if (!Number.isFinite(qtyUsed) || qtyUsed <= 0) {
        setToast('Quantità ingrediente non valida');
        return;
      }
      if (qtyUsed > lot.available + 0.000_000_001) {
        setToast(`Disponibilità insufficiente per ${foodById.get(lot.food_id)?.name ?? lot.food_id}`);
        return;
      }
      currentDrafts.push({ lot, qtyUsed });
    }
    const inputs: ProductionInputSaved[] = currentDrafts.map(({ lot, qtyUsed }) => {
      const food = foodById.get(lot.food_id);
      const supplier = lot.supplier_id ? supplierById.get(lot.supplier_id) : undefined;
      return {
        batch_lot: lot.batch_lot,
        food_id: lot.food_id,
        food_name: food?.name,
        supplier_id: lot.supplier_id,
        supplier_name: supplier?.name ?? null,
        document_id: lot.document_id,
        qty_used: qtyUsed,
        uom: lot.uom,
        allergens: food?.allergens ?? [],
      };
    });

    const allergensSet = new Set<string>();
    for (const i of inputs) for (const a of i.allergens ?? []) allergensSet.add(a);

    try {
      const id = crypto.randomUUID();
      const producedAt = new Date();
      const hoursNum = Number(shelfHours.replace(',', '.'));
      const shelfMin = Number.isFinite(hoursNum) && hoursNum > 0 ? Math.round(hoursNum * 60) : 72 * 60;
      const expiresAt = new Date(producedAt.getTime() + shelfMin * 60_000);
      // trace_token client-side per evitare race con sync (server lo ignora se già presente).
      const traceToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      const commandId = crypto.randomUUID();
      const operatorName = profile.full_name?.trim() || null;
      const commandInputs = currentDrafts.map(({ lot, qtyUsed }, index) => {
        const food = foodById.get(lot.food_id);
        return createProductionStockLine({
          foodId: lot.food_id,
          batchLot: lot.batch_lot,
          expirationDate: lot.expires,
          supplierId: lot.supplier_id,
          documentId: lot.document_id,
          storageUnitId: lot.storage_unit_id,
          qty: qtyUsed,
          sourceUom: lot.uom,
          stockUom: food?.default_uom?.trim() || lot.uom,
          unitCost: lot.unit_cost,
          currency: lot.currency,
          lineRef: `production:${id}:input:${index + 1}`,
          operatorName,
          lotAction: 'recorded_only',
        });
      });
      const finishedFoodPlan = await planProductionFinishedFood({
        tenantId: profile.active_tenant_id,
        recipeId: recipeId || null,
        collettivaEnabled,
        foods,
      });
      const masterEffects: StockCommandMasterEffectV1[] = [];
      let finishedFoodId: string | null = null;
      let finishedFoodUom: string | null = null;
      if (finishedFoodPlan.kind === 'existing') {
        finishedFoodId = finishedFoodPlan.food.id;
        finishedFoodUom = finishedFoodPlan.food.default_uom.trim();
        if (!finishedFoodUom) {
          throw new Error('Il prodotto finito associato non ha una UdM valida');
        }
      } else if (finishedFoodPlan.kind === 'create') {
        if (!canCreateFoods) {
          throw new Error(
            'Il prodotto finito non è ancora presente in magazzino. Chiedi a un responsabile di creare o collegare la scheda alimento prima di registrare la lavorazione.',
          );
        }
        if (!recipe) {
          throw new Error('Ricetta del prodotto finito non disponibile');
        }
        finishedFoodId = finishedFoodPlan.foodId;
        finishedFoodUom = outputUom.trim();
        if (!finishedFoodUom) {
          throw new Error('Unità di misura del prodotto finito richiesta');
        }
        masterEffects.push({
          table: 'foods',
          id: finishedFoodId,
          op: 'insert',
          base_version: 0,
          patch: {
            id: finishedFoodId,
            tenant_id: profile.active_tenant_id,
            name: recipe.name,
            description: 'Prodotto finito — generato automaticamente dalle lavorazioni',
            category: 'altro',
            default_uom: finishedFoodUom,
            storage_type: 'fresco',
            allergens: [...allergensSet],
            recipe_id: recipe.id,
          },
        });
      }

      const commandOutputs: ReturnType<typeof createProductionStockLine>[] = [];
      if (finishedFoodId && finishedFoodUom) {
        if (qty == null) {
          throw new Error('Quantità prodotta richiesta per caricare il prodotto finito');
        }
        const outputValue = carriedProductionOutputValue(commandInputs);
        commandOutputs.push(createProductionStockLine({
          foodId: finishedFoodId,
          batchLot: productionBatchLot(id),
          expirationDate: expiresAt.toISOString().slice(0, 10),
          supplierId: null,
          documentId: null,
          storageUnitId: null,
          qty,
          sourceUom: outputUom.trim(),
          stockUom: finishedFoodUom,
          unitCost: null,
          sourceLineTotal: outputValue.lineTotal,
          currency: outputValue.currency,
          lineRef: `production:${id}:output:1`,
          operatorName,
          lotAction: 'updated_active',
        }));
      }

      const command: StockCommandV1 = StockCommandV1Schema.parse({
        version: 1,
        command_id: commandId,
        tenant_id: profile.active_tenant_id,
        movement_class: 'production',
        economic_effect: 'internal',
        business_id: profile.active_business_id,
        production_id: id,
        reason: 'production_consumption',
        inputs: commandInputs,
        // T-425: i piatti della collettiva sono prodotti già destinati alla
        // spedizione e non costituiscono giacenza; gli ingredienti restano
        // comunque scaricati atomicamente. Negli altri tenant l'output è
        // obbligatorio e collegato deterministicamente alla ricetta.
        outputs: commandOutputs,
      });
      const snapshottedInputs = inputs.map((input) => ({
        ...input,
        // Receipt coordinates for a future all-or-nothing cancellation. They
        // live inside the existing JSON snapshot, so old rows remain readable
        // without a schema migration and are never reverse-engineered.
        stock_command_id: commandId,
        stock_command_event_count: commandInputs.length + commandOutputs.length,
      }));
      const productionEffect: StockCommandMasterEffectV1 = {
        table: 'productions',
        id,
        op: 'insert',
        base_version: 0,
        patch: {
          id,
          tenant_id: profile.active_tenant_id,
          business_id: profile.active_business_id,
          // T-066: operatore HACCP (produzione manuale da ProductionsPage).
          created_by: profile.id,
          produced_at: producedAt.toISOString(),
          output_name: name,
          output_qty: qty,
          output_uom: outputUom,
          recipe_id: recipeId || null,
          inputs: snapshottedInputs,
          allergens_aggregated: [...allergensSet],
          shelf_life_minutes: shelfMin,
          expires_at: expiresAt.toISOString(),
          notes: notes.trim() || null,
          trace_token: traceToken,
        },
      };
      masterEffects.push(productionEffect);
      pendingCreateEnvelope.current = {
        command,
        masterEffects,
      };

      await enqueueStockCommand.mutateAsync({
        command,
        master_effects: pendingCreateEnvelope.current.masterEffects,
      });

      setToast('Lavorazione salvata');
      resetForm();
      setModalOpen(false);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function cancelProduction(production: Production): Promise<void> {
    const tenantId = profile?.active_tenant_id;
    const businessId = profile?.active_business_id;
    if (!tenantId || !businessId || production.business_id !== businessId) {
      setToast('La lavorazione appartiene a un’altra sede');
      return;
    }
    if (production.archived_at) {
      setToast('La lavorazione risulta già annullata');
      return;
    }

    const pending = pendingCancelEnvelope.current;
    if (pending) {
      if (pending.productionId !== production.id) {
        setToast('Completa prima l’annullamento già in attesa');
        return;
      }
      try {
        await enqueueStockCommand.mutateAsync({
          command: pending.command,
          master_effects: pending.masterEffects,
        });
        pendingCancelEnvelope.current = null;
        setCancelTarget(null);
        setDetail(null);
        setToast('Lavorazione annullata · giacenze ripristinate');
      } catch (err) {
        setToast(toUserMessage(err));
      }
      return;
    }

    try {
      const {
        commandId: originalCommandId,
        expectedEventCount,
      } = productionReceiptCoordinates(production.inputs);
      const movements = productionReverseMovements({
        events: stockCommandEvents as ProductionCommandEvent[],
        commandId: originalCommandId,
        expectedEventCount,
        productionId: production.id,
        businessId,
      });
      const reverseCommand: StockCommandV1 = StockCommandV1Schema.parse({
        version: 1,
        command_id: crypto.randomUUID(),
        tenant_id: tenantId,
        movement_class: 'reverse',
        economic_effect: 'reversal',
        reason: `Annullamento lavorazione ${production.output_name}`,
        reverses_command_id: originalCommandId,
        movements,
      });
      const archiveEffect: StockCommandMasterEffectV1 = {
        table: 'productions',
        id: production.id,
        op: 'archive',
        base_version: production.version ?? 0,
        patch: {
          id: production.id,
          tenant_id: tenantId,
          archived_at: new Date().toISOString(),
        },
      };
      pendingCancelEnvelope.current = {
        productionId: production.id,
        command: reverseCommand,
        masterEffects: [archiveEffect],
      };
      // Il server applica archive + reverse nello stesso lock/receipt. Per una
      // produzione valorizzata storna prima l'output: se è già stato consumato
      // il saldo autoritativo abortisce tutto e non ripristina gli ingredienti.
      await enqueueStockCommand.mutateAsync({
        command: reverseCommand,
        master_effects: pendingCancelEnvelope.current.masterEffects,
      });
      pendingCancelEnvelope.current = null;
      setCancelTarget(null);
      setDetail(null);
      setToast('Lavorazione annullata · giacenze ripristinate');
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/warehouse" /></IonButtons>
          <IonTitle>Lavorazioni</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 96px' }}>
          {activeProductions.length === 0 ? (
            <div style={{ background: '#fff', padding: 16, borderRadius: 14, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
              Nessuna lavorazione. Tocca + per crearne una.
            </div>
          ) : (
            activeProductions
              .slice()
              .sort((a, b) => (b.produced_at ?? '').localeCompare(a.produced_at ?? ''))
              .map((p) => (
                <div
                  key={p.id}
                  onClick={() => setDetail(p)}
                  role="button"
                  style={{
                    background: '#fff',
                    borderRadius: 14,
                    borderLeft: `4px solid ${p.allergens_aggregated && p.allergens_aggregated.length > 0 ? '#b45309' : '#0066cc'}`,
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                    margin: '10px 0',
                    padding: '12px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: 40, height: 40, borderRadius: 12,
                      background: '#eff6ff', color: '#0066cc',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    <IonIcon icon={qrCodeOutline} style={{ fontSize: 22 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#0f172a' }}>{p.output_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      {p.output_qty ? `${p.output_qty} ${p.output_uom ?? ''} · ` : ''}
                      {p.inputs?.length ?? 0} ingredienti
                    </div>
                    {p.allergens_aggregated && p.allergens_aggregated.length > 0 && (
                      <div style={{ fontSize: 11, color: '#b45309', marginTop: 2 }}>
                        Allergeni: {p.allergens_aggregated.join(', ')}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>
                    {p.produced_at ? new Date(p.produced_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : ''}
                  </div>
                </div>
              ))
          )}
        </div>

        <IonFab slot="fixed" vertical="bottom" horizontal="end">
          <IonFabButton aria-label="Nuova lavorazione" onClick={() => setModalOpen(true)}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>

        <IonModal isOpen={modalOpen} onDidDismiss={() => { setModalOpen(false); resetForm(); }}>
          <IonHeader><IonToolbar>
            <IonTitle>Nuova lavorazione</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => { setModalOpen(false); resetForm(); }}><IonIcon icon={closeOutline} /></IonButton>
            </IonButtons>
          </IonToolbar></IonHeader>
          <IonContent className="ion-padding">
            {(recipes.length > 0 || !collettivaEnabled) && (
              <IonItem>
                <PickerTriggerField
                  label={collettivaEnabled ? 'Da ricetta (opzionale)' : 'Ricetta del prodotto finito *'}
                  placeholder="Tocca per scegliere la ricetta"
                  value={recipes.find((r) => r.id === recipeId)?.name}
                  onClick={() => setPickerOpen('recipe')}
                />
              </IonItem>
            )}
            <IonItem>
              <IonLabel position="stacked">Nome prodotto finito *</IonLabel>
              <IonInput value={outputName} placeholder="Lasagne alla bolognese" onIonInput={(ev) => setOutputName(String(ev.detail.value ?? ''))} />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">
                Quantità prodotta{collettivaEnabled ? '' : ' *'}
              </IonLabel>
              <IonInput type="number" inputmode="decimal" value={outputQty} onIonInput={(ev) => setOutputQty(String(ev.detail.value ?? ''))} />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Unità di misura</IonLabel>
              <IonSelect value={outputUom} onIonChange={(ev) => setOutputUom(String(ev.detail.value))}>
                <IonSelectOption value="kg">kg</IonSelectOption>
                <IonSelectOption value="pz">pz</IonSelectOption>
                <IonSelectOption value="porz">porz</IonSelectOption>
                <IonSelectOption value="l">l</IonSelectOption>
              </IonSelect>
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Shelf-life (ore) — definisce la scadenza</IonLabel>
              <IonInput
                type="number" inputmode="decimal" value={shelfHours}
                onIonInput={(ev) => setShelfHours(String(ev.detail.value ?? ''))}
              />
            </IonItem>

            <h4 style={{ marginTop: 24 }}>Ingredienti (lotti consumati)</h4>

            {inputDrafts.length > 0 && (
              <IonList>
                {inputDrafts.map((d) => {
                  const food = foodById.get(d.lot.food_id);
                  const supplier = d.lot.supplier_id ? supplierById.get(d.lot.supplier_id) : undefined;
                  const doc = d.lot.document_id ? docById.get(d.lot.document_id) : undefined;
                  return (
                    <IonItem key={d.key}>
                      <IonLabel>
                        <h3>{food?.name ?? d.lot.food_id.slice(0, 8)}</h3>
                        <p>
                          {d.qty_used} {d.lot.uom}
                          {d.lot.batch_lot ? ` — lotto ${d.lot.batch_lot}` : ''}
                        </p>
                        <p style={{ fontSize: 12, color: 'var(--ion-color-medium)' }}>
                          {supplier?.name ?? '—'}{doc?.document_number ? ` · DDT ${doc.document_number}` : ''}
                        </p>
                      </IonLabel>
                      <IonButton slot="end" fill="clear" color="danger" onClick={() => removeInputDraft(d.key)}>
                        <IonIcon icon={closeOutline} />
                      </IonButton>
                    </IonItem>
                  );
                })}
              </IonList>
            )}

            <IonItem>
              <PickerTriggerField
                label="Lotto disponibile"
                placeholder="Tocca per cercare un lotto"
                value={(() => {
                  const l = lots.find((x) => x.key === selectedLotKey);
                  if (!l) return undefined;
                  const food = foodById.get(l.food_id);
                  return `${food?.name ?? l.food_id.slice(0, 6)}${l.batch_lot ? ` · L.${l.batch_lot}` : ''}`;
                })()}
                onClick={() => setPickerOpen('lot')}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Quantità usata</IonLabel>
              <IonInput type="number" inputmode="decimal" value={qtyDraft} onIonInput={(ev) => setQtyDraft(String(ev.detail.value ?? ''))} />
            </IonItem>
            <IonButton expand="block" fill="outline" onClick={addInputDraft} style={{ marginTop: 8 }}>
              <IonIcon icon={add} slot="start" /> Aggiungi ingrediente
            </IonButton>

            <IonItem style={{ marginTop: 16 }}>
              <IonLabel position="stacked">Note</IonLabel>
              <IonTextarea rows={2} value={notes} onIonInput={(ev) => setNotes(String(ev.detail.value ?? ''))} />
            </IonItem>

            <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
              <IonButton
                expand="block"
                onClick={() => void saveProduction()}
                disabled={enqueueStockCommand.isPending}
                style={{ flex: 1 }}
              >
                Salva
              </IonButton>
              <IonButton expand="block" fill="outline" onClick={() => { setModalOpen(false); resetForm(); }} style={{ flex: 1 }}>Annulla</IonButton>
            </div>
          </IonContent>
        </IonModal>

        {/* Selettore ricerca: RICETTA da cui pre-compilare la produzione. */}
        <SearchPickerModal
          isOpen={pickerOpen === 'recipe'}
          title="Scegli una ricetta"
          searchPlaceholder="Cerca ricetta…"
          selectedId={recipeId}
          emptyHint="Nessuna ricetta disponibile."
          primaryAction={collettivaEnabled
            ? {
                label: 'Nessuna — parti da zero',
                onClick: () => { applyRecipe(''); setPickerOpen(null); },
              }
            : undefined}
          items={recipes
            // T-441: escludi archiviate e varianti dieta (ricette figlie) dal picker.
            .filter((r) => !r.archived_at && r.variant_of_recipe_id == null)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((r) => ({ id: r.id, label: r.name }))}
          onSelect={(id) => { applyRecipe(id); setPickerOpen(null); }}
          onClose={() => setPickerOpen(null)}
        />

        {/* Selettore ricerca: LOTTO consumato. */}
        <SearchPickerModal
          isOpen={pickerOpen === 'lot'}
          title="Scegli il lotto"
          searchPlaceholder="Cerca lotto o alimento…"
          selectedId={selectedLotKey}
          emptyHint="Nessun lotto disponibile."
          items={lots
            .filter((l) => !inputDrafts.some((d) => d.key === l.key))
            .map((l) => {
              const food = foodById.get(l.food_id);
              return {
                id: l.key,
                label: `${food?.name ?? l.food_id.slice(0, 6)}${l.batch_lot ? ` · L.${l.batch_lot}` : ''}`,
                sublabel: `disp. ${l.available.toFixed(2)} ${l.uom}`,
              };
            })}
          onSelect={(id) => { setSelectedLotKey(id); setPickerOpen(null); }}
          onClose={() => setPickerOpen(null)}
        />

        <IonModal isOpen={!!detail} onDidDismiss={() => setDetail(null)}>
          <IonHeader><IonToolbar>
            <IonTitle>{detail?.output_name ?? ''}</IonTitle>
            <IonButtons slot="end"><IonButton onClick={() => setDetail(null)}><IonIcon icon={closeOutline} /></IonButton></IonButtons>
          </IonToolbar></IonHeader>
          <IonContent className="ion-padding">
            {detail && (
              <>
                <IonItem lines="none">
                  <IonLabel>
                    <h2>Catena di tracciabilità</h2>
                    <p>Token QR: <code>{detail.trace_token}</code></p>
                    <p>Prodotto il {new Date(detail.produced_at).toLocaleString('it-IT')}</p>
                    {detail.expires_at && (
                      <p style={{ color: 'var(--ion-color-warning)' }}>
                        <b>Scade:</b> {new Date(detail.expires_at).toLocaleString('it-IT')}
                      </p>
                    )}
                  </IonLabel>
                </IonItem>
                <IonItem button detail routerLink={`/warehouse/trace/${detail.trace_token}`} onClick={() => setDetail(null)}>
                  <IonIcon icon={qrCodeOutline} slot="start" color="primary" />
                  <IonLabel>
                    <h3>Vedi catena di tracciabilità</h3>
                    <p>Ingredienti, lotti, fornitori, DDT collegati</p>
                  </IonLabel>
                </IonItem>
                <IonItem button onClick={() => window.open(`${TRACE_BASE_URL}/t/${encodeURIComponent(detail.trace_token)}`, '_blank')}>
                  <IonIcon icon={qrCodeOutline} slot="start" />
                  <IonLabel>Apri pagina pubblica QR (per cliente)</IonLabel>
                </IonItem>
                <IonButton
                  expand="block"
                  fill="outline"
                  color="danger"
                  disabled={enqueueStockCommand.isPending}
                  onClick={() => setCancelTarget(detail)}
                  style={{ marginTop: 16 }}
                >
                  <IonIcon icon={trashOutline} slot="start" />
                  Annulla lavorazione
                </IonButton>
                {detail.allergens_aggregated && detail.allergens_aggregated.length > 0 && (
                  <IonItem>
                    <IonLabel>
                      <h3>Allergeni aggregati</h3>
                      <p>{detail.allergens_aggregated.join(', ')}</p>
                    </IonLabel>
                  </IonItem>
                )}
                {/* T-148 — Abbattimento mostrato dalla RICETTA d'origine (sorgente
                    unica). Non più configurato sulla lavorazione. */}
                {(() => {
                  const summary = detail.recipe_id
                    ? chillingSummaryFromRecipe(recipeById.get(detail.recipe_id))
                    : null;
                  if (!summary) return null;
                  return (
                    <IonItem>
                      <IonIcon icon={snowOutline} slot="start" color="primary" aria-hidden="true" />
                      <IonLabel className="ion-text-wrap">
                        <h3>Abbattimento (da ricetta)</h3>
                        <p>{summary}</p>
                      </IonLabel>
                    </IonItem>
                  );
                })()}
                <h3 style={{ marginTop: 16 }}>Ingredienti</h3>
                <IonList>
                  {(detail.inputs ?? []).map((i, idx) => {
                    const doc = i.document_id ? docById.get(i.document_id) : undefined;
                    return (
                      <IonItem key={idx}>
                        <IonLabel>
                          <h3>{i.food_name ?? '—'}</h3>
                          <p>{i.qty_used} {i.uom}{i.batch_lot ? ` · lotto ${i.batch_lot}` : ''}</p>
                          <p style={{ fontSize: 12 }}>
                            {i.supplier_name ?? '—'}
                            {doc?.document_number ? ` · DDT ${doc.document_number}` : ''}
                            {doc?.document_date ? ` del ${doc.document_date}` : ''}
                          </p>
                        </IonLabel>
                      </IonItem>
                    );
                  })}
                </IonList>
              </>
            )}
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={!!cancelTarget}
          header="Annulla lavorazione"
          message={`Vuoi annullare “${cancelTarget?.output_name ?? 'questa lavorazione'}”? Verranno stornati insieme output e ingredienti. Se il prodotto finito è già stato utilizzato, l’operazione sarà bloccata senza modifiche.`}
          buttons={[
            { text: 'No', role: 'cancel' },
            {
              text: 'Sì, annulla',
              role: 'destructive',
              handler: () => {
                if (cancelTarget) void cancelProduction(cancelTarget);
              },
            },
          ]}
          onDidDismiss={() => setCancelTarget(null)}
        />
        <IonToast isOpen={!!toast} message={toast ?? ''} duration={1800} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
